import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { genericAPIs, crmAPI } from '../../services/api';
import { FaInstagram } from 'react-icons/fa';
import SkillSelector from '../common/SkillSelector';
import { Skill } from '../../types';

const InstagramIcon = FaInstagram as any;

interface AddVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onOpenSyncInstagram?: () => void;
}

const generateVideoThumbnail = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    
    const objUrl = URL.createObjectURL(file);
    video.src = objUrl;
    
    video.onloadeddata = () => {
      video.currentTime = Math.min(1, video.duration / 2);
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = 320 / Math.max(1, video.videoWidth);
        canvas.width = 320;
        canvas.height = Math.max(1, video.videoHeight * scale);

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl);
        } else {
          resolve('');
        }
      } catch (e) {
        console.error('Failed to capture video frame:', e);
        resolve('');
      } finally {
        URL.revokeObjectURL(objUrl);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objUrl);
      resolve('');
    };
  });
};

const AddVideoModal: React.FC<AddVideoModalProps> = ({ isOpen, onClose, onSuccess, onOpenSyncInstagram }) => {
  const [activeTab, setActiveTab] = useState<'link' | 'upload'>('link');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState<Skill[]>([]);
  
  // Google Drive upload states
  const [driveConnected, setDriveConnected] = useState(true);
  const [driveAuthUrl, setDriveAuthUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSkills();
      if (activeTab === 'upload') {
        checkDriveStatus();
      }
    }
  }, [isOpen, activeTab]);

  const checkDriveStatus = async () => {
    try {
      const res = await crmAPI.getGoogleDriveStatus();
      setDriveConnected(res.data?.connected || false);
      if (!res.data?.connected) {
        const authRes = await crmAPI.getGoogleDriveAuthURL();
        setDriveAuthUrl(authRes.data?.url || '');
      }
    } catch (e) {
      console.error('Failed to check Google Drive status', e);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await genericAPIs.getUserRoleSkills('all');
      setSkills(response.data.data.skills || []);
    } catch (error) {
      console.error('Failed to fetch skills', error);
    }
  };

  const handleTagsChange = (skillIds: number[]) => {
    const selected = skills.filter(s => skillIds.includes(s.id));
    setSelectedTags(selected);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (activeTab === 'link') {
      if (!url || !title) {
        toast.error('URL and Title are required');
        return;
      }

      const lowerUrl = url.toLowerCase();
      if (!lowerUrl.includes('youtube.com') && !lowerUrl.includes('youtu.be') && !lowerUrl.includes('drive.google.com')) {
        toast.error('Invalid URL. Please paste a YouTube or Google Drive link.');
        return;
      }

      setLoading(true);
      try {
        await genericAPIs.addVideo({
          youtube_url: url,
          title,
          description,
          tags: selectedTags.map(s => s.name)
        });
        toast.success('Video added successfully');
        onSuccess();
        onClose();
        // Reset form
        setUrl('');
        setTitle('');
        setDescription('');
        setSelectedTags([]);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to add video');
      } finally {
        setLoading(false);
      }
    } else {
      if (!file || !title) {
        toast.error('Video file and Title are required');
        return;
      }
      if (!driveConnected) {
        toast.error('Please connect Google Drive first');
        return;
      }

      setLoading(true);
      setUploadProgress(0);
      try {
        // 1. Generate video thumbnail client-side
        setIsProcessing(true);
        const base64Thumbnail = await generateVideoThumbnail(file);
        setIsProcessing(false);

        // 2. Initiate Resumable Upload
        const initRes = await genericAPIs.initiateProfileVideoUpload({
          filename: file.name,
          size: file.size,
          content_type: file.type || 'video/mp4'
        });

        const uploadUrl = initRes.data?.upload_url;
        if (!uploadUrl) {
          throw new Error("Failed to get Google Drive upload URL");
        }

        // 3. Perform Resumable Upload
        const cleanAxios = axios.create();
        const driveRes = await cleanAxios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'video/mp4',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(pct);
            }
          }
        });

        const fileId = driveRes.data?.id;
        if (!fileId) {
          throw new Error("Failed to retrieve Google Drive File ID");
        }

        setIsProcessing(true);
        // 4. Complete upload
        await genericAPIs.completeProfileVideoUpload({
          file_id: fileId,
          filename: file.name,
          title,
          description,
          tags: selectedTags.map(s => s.name),
          thumbnail: base64Thumbnail
        });

        toast.success('Video uploaded to Google Drive & added successfully!');
        onSuccess();
        onClose();
        
        // Reset
        setFile(null);
        setTitle('');
        setDescription('');
        setSelectedTags([]);
        setUploadProgress(null);
      } catch (error: any) {
        console.error(error);
        const errorMsg = error.response?.data?.error || error.message || 'Failed to upload video';
        toast.error(errorMsg);
      } finally {
        setLoading(false);
        setIsProcessing(false);
        setUploadProgress(null);
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Add Profile Video
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="grid grid-cols-3 border border-gray-200 dark:border-gray-700 bg-gray-100/80 dark:bg-gray-800/60 p-1.5 gap-1.5 rounded-xl mb-6">
                  <button
                    type="button"
                    className={`py-2 px-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center space-x-1 ${
                      activeTab === 'upload'
                        ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-sm border border-gray-200/60 dark:border-gray-700'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                    onClick={() => setActiveTab('upload')}
                  >
                    <span>Upload File</span>
                  </button>
                  <button
                    type="button"
                    className={`py-2 px-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center space-x-1 ${
                      activeTab === 'link'
                        ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-sm border border-gray-200/60 dark:border-gray-700'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                    onClick={() => setActiveTab('link')}
                  >
                    <span>YouTube/Drive Link</span>
                  </button>
                  {onOpenSyncInstagram && (
                    <button
                      type="button"
                      className="py-2 px-2 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 hover:text-pink-600 dark:hover:text-pink-400 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all flex items-center justify-center space-x-1"
                      onClick={() => {
                        onClose();
                        onOpenSyncInstagram();
                      }}
                    >
                      <InstagramIcon className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                      <span>Sync Instagram</span>
                    </button>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {activeTab === 'link' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Video URL</label>
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:border-gray-300 focus:ring-0 sm:text-sm"
                        placeholder="Paste YouTube or Google Drive URL"
                        required={activeTab === 'link'}
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {!driveConnected ? (
                        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 flex flex-col items-center text-center">
                          <p className="text-sm text-amber-800 font-semibold mb-2">Google Drive Not Connected</p>
                          <p className="text-xs text-amber-700 mb-3">You need to connect Google Drive to upload videos directly from your device.</p>
                          <a
                            href={driveAuthUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
                          >
                            🔗 Connect Google Drive
                          </a>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-xl border border-blue-200 dark:border-blue-800/60 flex items-start gap-2.5">
                            <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-bold text-blue-800 dark:text-blue-200">
                                Google Drive Storage Integration
                              </p>
                              <p className="text-[10px] md:text-[11px] text-blue-700 dark:text-blue-300 mt-0.5 leading-relaxed">
                                Local videos are uploaded directly to your own Google Drive and saved in the **"ClickCollab Profile Videos"** folder.
                              </p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Select Video File</label>
                          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center hover:border-indigo-500 transition-colors cursor-pointer relative">
                            <input
                              type="file"
                              accept="video/*"
                              onChange={(e) => {
                                const selected = e.target.files?.[0];
                                if (selected) {
                                  setFile(selected);
                                  if (!title) {
                                    const nameWithoutExt = selected.name.substring(0, selected.name.lastIndexOf('.')) || selected.name;
                                    setTitle(nameWithoutExt);
                                  }
                                }
                              }}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              required={activeTab === 'upload'}
                            />
                            <ArrowUpTrayIcon className="w-10 h-10 text-gray-400 mb-2" />
                            {file ? (
                              <div className="text-center">
                                <p className="text-sm font-semibold text-gray-700 truncate max-w-[250px]">{file.name}</p>
                                <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 text-center">Drag & drop or click to choose video file</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:border-gray-300 focus:ring-0 sm:text-sm"
                      placeholder="Video Title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:border-gray-300 focus:ring-0 sm:text-sm"
                      placeholder="Optional description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags (Specializations)</label>
                    <SkillSelector
                      options={skills}
                      selectedSkillIds={selectedTags.map(s => s.id)}
                      onChange={handleTagsChange}
                      maxSkills={5}
                    />
                  </div>

                  {/* Progress & Processing layout */}
                  {loading && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col items-center">
                      {isProcessing ? (
                        <div className="flex items-center space-x-2">
                          <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm font-semibold text-gray-700">Processing video frame & metadata...</span>
                        </div>
                      ) : (
                        uploadProgress !== null && (
                          <div className="w-full">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-semibold text-indigo-600">Uploading to Google Drive</span>
                              <span className="text-xs font-semibold text-indigo-600">{uploadProgress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={loading}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || (activeTab === 'upload' && (!file || !driveConnected))}
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none disabled:opacity-50"
                    >
                      {loading ? 'Processing...' : activeTab === 'upload' ? 'Upload & Add Video' : 'Add Video'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddVideoModal;
